(ns common.i18n.core
  (:require [clojure.string :as string]
            [common.localization-en :as en]
            [common.localization-fi :as fi]
            [common.util :as cu]))

(def supported-locales #{:en :fi})

(defn dict [lang]
  (case lang
    :en en/translations
    :fi fi/translations
    ;; fallback: English always exists
    en/translations))

(defn t
  "Lookup with fallback: lang → :en. Returns nil if not found."
  ([k] (t k :en))
  ([k lang]
   (get (dict lang) k)))

(defn missing-keys
  "Given a set of keys, return the ones missing in the dict for lang."
  [lang ks]
  (let [d (dict lang)]
    (into #{}
          (remove #(contains? d %))
          ks)))

;; -----------------------------------------------------------------------------
;; Internals
;; -----------------------------------------------------------------------------

(def ^:private default-locales [:en])

(defn- normalize-code
  "Accepts keyword or \"ns/name\" string and returns a stable \"ns/name\" string."
  [code]
  (cond
    (keyword? code) (let [ns (namespace code)
                          n (name code)]
                      (if ns (str ns "/" n) n))
    (string? code) code
    :else (str code)))

(defn- titleize
  "screening-status/inactive -> \"Inactive\" ; \"pep_status\" -> \"Pep status\"."
  [s]
  (let [l-case-s (some-> s
                         (string/replace #"[_\-\.]+" " ")
                         (string/replace #".*/" "")         ; drop namespace if present
                         (string/lower-case))]
    (when-not (cu/blank-value? l-case-s)
      (some->> (string/split l-case-s #" ")
               (keep (fn [s]
                       (when-not (cu/blank-value? s)
                         s)))
               (map #(str (string/upper-case (subs % 0 1))
                          (subs % 1)))
               (string/join " ")))))

(defn- dict-label
  "Try tenant dictionaries: expects tenant-schema like
   {:i18n/dictionaries {:en {\"screening-status/inactive\" \"Inactive\" ...}
                        :fi {...}}}
   Returns nil if not found."
  [tenant-schema locale code-str]
  (get-in tenant-schema [:i18n/dictionaries locale code-str]))

(defn- field-key->label-key
  "For a flattened base key like :flat.country and locale :en,
   produce a label key under a separate root that won't collide with flat.* mapping."
  [k locale]
  (let [base (name k)                                       ; e.g. "flat.country"
        safe (string/replace base #"\." "_")]               ; "flat_country"
    (keyword (str "labels." safe "_" (name locale)))))      ; labels.flat_country_en

(defn- keyword-like?
  "We consider keywords or \"ns/name\" strings as labelable codes."
  [v]
  (or (keyword? v)
      (and (string? v)
           (re-find #".+/.+" v))))

;; -----------------------------------------------------------------------------
;; Public API
;; -----------------------------------------------------------------------------

(defn label
  "Resolve a user-facing label for enum/keyword `code` in `locale`.
   Tries tenant-schema dictionaries first; falls back to a simple titleized name."
  [tenant-schema locale code]
  (let [code-str (normalize-code code)]
    (or (dict-label tenant-schema locale code-str)
        (titleize code-str))))

(defn add-labels
  "Walk a *flat* map `m` (e.g. output of your flatten-for-grid) and, for each
   keyword-like value (keyword or \"ns/name\" string), add sibling
   \".label.<locale>\" fields for the provided locales.
   Example:
     :screening_profile.screening_status -> \"screening-status/inactive\"
     adds :screening_profile.screening_status.label.en -> \"Inactive\"."
  ([opts m]
   (add-labels (merge {:locales default-locales} opts) m {}))
  ([{:keys [tenant-schema locales]} m acc]
   (reduce-kv
    (fn [a k v]
      (cond (keyword-like? v)
            (let [labels (into {}
                               (for [loc locales]
                                    [(field-key->label-key k loc) (label tenant-schema loc v)]))]
                 (merge a labels))
            :else
            a))
    acc m)))
